import { ChangeDetectionStrategy, Component, TemplateRef, ViewChild, computed, signal } from '@angular/core';
import { UserApi } from 'api/user/user-api.js';
import { PackageDb } from 'lib/cosmos/package/packageDb.js';
import { UserDb } from 'lib/cosmos/user/userDb.js';
import { guidHelper } from 'lib/helpers/guid-helper.class.js';
import { IButton } from 'lib/ui/buttons/index.js';
import { IDialogButtonsContext, SelectionDialogItem } from 'lib/ui/dialog/index.js';
import { ListItem } from 'lib/ui/list/index.js';
import { IImage } from 'lib/ui/ui-models.js';
import { map } from 'rxjs';
import { TypeCoreComponent } from 'src/app/pages/type-routes/type-core.component.js';

type ExtendedPackage = UserDb.IUserAssignedPackage & {
	readonly name: string;
	readonly description?: string;
	readonly price?: string;
	readonly avatar?: IImage;
	readonly buttons: IButton[];
};

@Component({
	selector: 'mod-client-packages',
	templateUrl: './client-packages.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class ClientPackagesComponent extends TypeCoreComponent<UserDb.IUserItem> {
	@ViewChild('newPackageTemplate', { static: true }) newPackageTemplate!: TemplateRef<any>;

	private readonly allPackages = signal<PackageDb.IPackageItem[] | undefined>(undefined);
	private readonly assignedPackages = signal<ExtendedPackage[] | undefined>(undefined);

	public readonly assignedPackageListItems = computed<readonly ListItem<ExtendedPackage>[] | undefined>(() => {
		const assignedPackages = this.assignedPackages();

		if (!assignedPackages) {
			return undefined;
		}

		return assignedPackages.map<ListItem<ExtendedPackage>>((m) => {
			return {
				data: m,
				id: m.id,
				title: m.name,
				subtitle: m.description,
				avatar: m.avatar,
				buttons: m.buttons,
				tag: m.price ? { text: m.price, tagType: 'basic' } : undefined
			};
		});
	});

	public readonly availablePackagesForAssignment = computed<readonly PackageDb.IPackageItem[]>(() => {
		const allPackages = this.allPackages();
		const assignedPackages = this.assignedPackages();

		if (!allPackages || !assignedPackages) {
			return [];
		}

		return allPackages.filter((packageItem) => {
			if (assignedPackages.find((m) => m.packageId === packageItem.id)) {
				return false;
			}
			return true;
		});
	});

	public readonly assignPackagesButtons = computed<readonly IButton[]>(() => {
		const availablePackages = this.availablePackagesForAssignment();

		if (!availablePackages) {
			return [];
		}

		const buttons: IButton[] = [
			{
				title: 'Assign package',
				buttonType: 'primary',
				buttonSize: 'default',
				click: () => {
					this.openAssignPackageDialog(availablePackages);
				}
			}
		];

		return buttons;
	});

	constructor() {
		super();

		this.dataInit = () => {
			this.initPackages();
		};
	}

	handleCreatePackage(packageItem: PackageDb.IPackageItem): void {
		this.allPackages.update((allTasks) => {
			return [...(allTasks ?? []), packageItem];
		});

		// add package to current linked items
		super.addLinkedItemToSharedData(packageItem);

		this.dependencies.dialogService.closeAllDialogs();
		this.assignPackage(packageItem);
	}

	private openCreateCustomPackageDialog(): void {
		this.dependencies.dialogService.openDialog({
			title: 'Create package',
			subtitle: 'Packages represent services that you offer to your clients',
			size: 'large',
			template: () => this.newPackageTemplate
		});
	}

	private getSelectPackageDialogButtons(context: IDialogButtonsContext): IButton[] {
		return [
			{
				buttonSize: 'default',
				buttonType: 'basic',
				title: 'Create package',
				click: () => {
					context.closeDialog();
					this.openCreateCustomPackageDialog();
				}
			}
		];
	}

	private initPackages(): void {
		super.subscribeToObservable(
			this.dependencies.httpService
				.get<PackageDb.IPackageItem[]>((builder) =>
					builder.withCustomApiRoute('package', 'getPackages').build()
				)
				.pipe(
					map((response) => {
						this.allPackages.set(response.data);
						this.updateAssignedPackages();
					})
				)
		);
	}

	private updateAssignedPackages(): void {
		const sharedData = this.sharedData();
		if (!sharedData) {
			return;
		}
		const allPackages = this.allPackages();

		const currentlyAssignedPackages = sharedData.data.assignedPackages ?? [];

		this.assignedPackages.set(
			currentlyAssignedPackages.map((assignedPackage) => {
				const linkedPackage = allPackages?.find((m) => m.id === assignedPackage.packageId);

				const extendedItem: ExtendedPackage = {
					...assignedPackage,
					description: linkedPackage?.description,
					name: super.getRequiredItemName(linkedPackage?.system.name),
					price: linkedPackage?.price,
					avatar: linkedPackage?.avatar?.[0]?.image,
					buttons: [
						{
							iconOnly: true,
							icon: 'remove',
							title: `Remove`,
							confirm: {
								confirmMode: 'delete',
								text: `Are you sure to remove this package from client?`
							},
							click: () => {
								this.removePackage(assignedPackage.id);
							}
						}
					]
				};

				return extendedItem;
			})
		);
	}

	private openAssignPackageDialog(availablePackages: readonly PackageDb.IPackageItem[]): void {
		this.dependencies.dialogService.openSelectionDialog({
			title: `Select package`,
			subtitle: `Packages represent services that you offer to your clients`,
			buttons: (context) => this.getSelectPackageDialogButtons(context),
			items: availablePackages.map((packageItem) => {
				const selectionItem: SelectionDialogItem<PackageDb.IPackageItem> = {
					data: packageItem,
					id: packageItem.id,
					title: packageItem.system.name,
					tag: packageItem.price ? { text: packageItem.price, tagType: 'basic' } : undefined,
					avatar: packageItem.avatar?.[0]?.image
				};

				return selectionItem;
			}),
			select: (item) => {
				this.assignPackage(item);
			}
		});
	}

	private removePackage(packageId: string): void {
		const sharedData = this.sharedData();

		if (!sharedData) {
			return;
		}

		this.saveAssignedPackages((sharedData.data.assignedPackages ?? []).filter((m) => m.id !== packageId));
	}

	private assignPackage(packageItem: PackageDb.IPackageItem): void {
		const sharedData = this.sharedData();

		if (!sharedData) {
			return;
		}

		this.saveAssignedPackages([
			...(sharedData.data.assignedPackages ?? []),
			{ id: guidHelper.shortGuid(), packageId: packageItem.id }
		]);
	}

	private saveAssignedPackages(assignedPackages: readonly UserDb.IUserAssignedPackage[]): void {
		const sharedData = this.sharedData();

		if (!sharedData) {
			return;
		}

		sharedData.data.assignedPackages = assignedPackages;
		this.updateAssignedPackages();

		super.subscribeToObservable(
			this.dependencies.httpService
				.post<UserDb.IUserItem, UserApi.IEditPackagesRequest>((builder) =>
					builder
						.withCustomApiRoute('user', 'editPackages')
						.withId(sharedData.data.id)
						.withData({
							packages: assignedPackages
						})
						.build()
				)
				.pipe(
					map((response) => {
						// update user
						super.updateSharedData({
							data: response.data,
							linkedItems: response.linkedItems
						});

						super.notifySaved('Package');
					})
				)
		);
	}
}
